/**
 * Video Controls Component
 * Control buttons for video call (mute, camera, screen share, leave)
 */

import React from 'react';
import { Button, Space, Tooltip } from 'antd';
import {
  AudioOutlined,
  AudioMutedOutlined,
  VideoCameraOutlined,
  VideoCameraAddOutlined,
  DesktopOutlined,
  CloseCircleOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import type { VideoControlsProps } from '@/api/types/webrtc.types';
import './VideoControls.css';

export const VideoControls: React.FC<VideoControlsProps> = ({
  isAudioEnabled,
  isVideoEnabled,
  isScreenSharing,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onLeave,
  disabled = false,
}) => {
  return (
    <div className="video-controls">
      <Space size="middle">
        {/* Audio toggle */}
        <Tooltip
          title={isAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
        >
          <Button
            type={isAudioEnabled ? 'default' : 'primary'}
            danger={!isAudioEnabled}
            icon={isAudioEnabled ? <AudioOutlined /> : <AudioMutedOutlined />}
            onClick={onToggleAudio}
            disabled={disabled}
            size="large"
            className={`control-button ${!isAudioEnabled ? 'muted' : ''}`}
          >
            {isAudioEnabled ? 'Mute' : 'Unmute'}
          </Button>
        </Tooltip>

        {/* Video toggle */}
        <Tooltip title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}>
          <Button
            type={isVideoEnabled ? 'default' : 'primary'}
            danger={!isVideoEnabled}
            icon={
              isVideoEnabled ? (
                <VideoCameraOutlined />
              ) : (
                <VideoCameraAddOutlined />
              )
            }
            onClick={onToggleVideo}
            disabled={disabled}
            size="large"
            className={`control-button ${!isVideoEnabled ? 'camera-off' : ''}`}
          >
            {isVideoEnabled ? 'Stop Video' : 'Start Video'}
          </Button>
        </Tooltip>

        {/* Screen share toggle */}
        <Tooltip
          title={isScreenSharing ? 'Stop sharing screen' : 'Share screen'}
        >
          <Button
            type={isScreenSharing ? 'primary' : 'default'}
            icon={
              isScreenSharing ? <CloseCircleOutlined /> : <DesktopOutlined />
            }
            onClick={onToggleScreenShare}
            disabled={disabled}
            size="large"
            className={`control-button ${isScreenSharing ? 'screen-sharing' : ''}`}
          >
            {isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
          </Button>
        </Tooltip>

        {/* Leave call */}
        <Tooltip title="Leave call">
          <Button
            type="primary"
            danger
            icon={<PhoneOutlined rotate={135} />}
            onClick={onLeave}
            disabled={disabled}
            size="large"
            className="control-button leave-button"
          >
            Leave
          </Button>
        </Tooltip>
      </Space>
    </div>
  );
};
